// Delivery Hub queries dashboard: all schools' queries by category, status and owner
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AlertCircle, CheckCircle, Clock, Loader2, RefreshCw, Search, X } from "lucide-react";
import {
  ClientQuery,
  QueryCategory,
  QueryCountRow,
  QueryDashboardStats,
  QueryFilters,
  QueryOwner,
  getAllQueries,
  getQueryCategories,
  getQueryDashboardStats,
  getQueryOwners,
} from "@/actions/delivery-hub";
import { AttachmentList } from "@/components/queries/QueryAttachments";
import QueryRoutingControls from "@/components/queries/QueryRoutingControls";

const STATUS_BADGE: Record<string, { label: string; className: string; icon: React.ElementType }> = {
  open: { label: "Open", className: "bg-amber-100 text-amber-700", icon: AlertCircle },
  in_progress: { label: "In progress", className: "bg-blue-100 text-blue-700", icon: Clock },
  resolved: { label: "Resolved", className: "bg-green-100 text-green-700", icon: CheckCircle },
};

const StatusBadge = ({ status }: { status: string }) => {
  const cfg = STATUS_BADGE[status] || STATUS_BADGE.open;
  const Icon = cfg.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full ${cfg.className}`}>
      <Icon size={12} />
      {cfg.label}
    </span>
  );
};

const StatTile = ({
  label,
  value,
  active,
  onClick,
  tone = "text-gray-900",
}: {
  label: string;
  value: number;
  active?: boolean;
  onClick?: () => void;
  tone?: string;
}) => (
  <button
    type="button"
    onClick={onClick}
    className={`flex-1 min-w-[120px] text-left bg-white rounded-xl border px-4 py-3 transition-colors ${
      active ? "border-brand ring-1 ring-brand" : "border-gray-200 hover:border-gray-300"
    }`}
  >
    <div className="text-xs text-gray-500">{label}</div>
    <div className={`text-2xl font-semibold mt-0.5 ${tone}`}>{value}</div>
  </button>
);

/** Counts table; clicking a row filters the list to that category / owner */
const BreakdownTable = ({
  title,
  rows,
  selectedId,
  onSelect,
}: {
  title: string;
  rows: QueryCountRow[];
  selectedId?: string;
  onSelect: (id: string) => void;
}) => (
  <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
    <div className="px-4 py-3 border-b border-gray-100 text-sm font-medium text-gray-900">{title}</div>
    <table className="w-full text-sm">
      <thead>
        <tr className="text-xs text-gray-500">
          <th className="text-left font-medium px-4 py-2">Name</th>
          <th className="text-right font-medium px-2 py-2">Open</th>
          <th className="text-right font-medium px-2 py-2">In progress</th>
          <th className="text-right font-medium px-2 py-2">Resolved</th>
          <th className="text-right font-medium px-4 py-2">Total</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => {
          const id = r.id === null ? "none" : String(r.id);
          const active = selectedId === id;
          return (
            <tr
              key={id}
              onClick={() => onSelect(active ? "" : id)}
              className={`cursor-pointer border-t border-gray-50 ${active ? "bg-brand/5" : "hover:bg-gray-50"}`}
            >
              <td className="px-4 py-2 text-gray-900">{r.name}</td>
              <td className="px-2 py-2 text-right tabular-nums text-gray-700">{r.open}</td>
              <td className="px-2 py-2 text-right tabular-nums text-gray-700">{r.in_progress}</td>
              <td className="px-2 py-2 text-right tabular-nums text-gray-500">{r.resolved}</td>
              <td className="px-4 py-2 text-right tabular-nums font-medium text-gray-900">{r.total}</td>
            </tr>
          );
        })}
      </tbody>
    </table>
  </div>
);

const DeliveryHubQueries = () => {
  const [filters, setFilters] = useState<QueryFilters>({ status: "", category_id: "", owner_id: "" });
  const [searchInput, setSearchInput] = useState("");
  const [stats, setStats] = useState<QueryDashboardStats | null>(null);
  const [queries, setQueries] = useState<ClientQuery[]>([]);
  const [categories, setCategories] = useState<QueryCategory[]>([]);
  const [owners, setOwners] = useState<QueryOwner[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getQueryCategories().then(setCategories);
    getQueryOwners().then(setOwners);
  }, []);

  // Search applies after typing pauses
  useEffect(() => {
    const t = setTimeout(() => setFilters((f) => ({ ...f, search: searchInput.trim() })), 400);
    return () => clearTimeout(t);
  }, [searchInput]);

  const load = useCallback(async () => {
    setLoading(true);
    // Breakdown tables ignore their own dimension so every row stays visible
    const [list, s] = await Promise.all([
      getAllQueries(filters),
      getQueryDashboardStats({ ...filters, status: "", category_id: "", owner_id: "" }),
    ]);
    setQueries(list);
    setStats(s);
    setLoading(false);
  }, [filters]);

  useEffect(() => {
    load();
  }, [load]);

  const setFilter = (key: keyof QueryFilters, value: string) => setFilters((f) => ({ ...f, [key]: value }));
  const hasFilters = Boolean(
    filters.status || filters.category_id || filters.owner_id || filters.from || filters.to || filters.search
  );
  const totals = stats?.totals;

  const replaceQuery = (updated: ClientQuery) => {
    setQueries((prev) =>
      prev.map((q) => (q.id === updated.id ? { ...q, ...updated, school_name: q.school_name, days_open: q.days_open } : q))
    );
    getQueryDashboardStats({ ...filters, status: "", category_id: "", owner_id: "" }).then(setStats);
  };

  const inputClass =
    "px-3 py-1.5 text-sm border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-brand focus:border-brand";

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="flex items-center justify-between mb-5">
        <div>
          <h1 className="text-xl font-semibold text-gray-900">Client queries</h1>
          <p className="text-sm text-gray-500">All schools, by category, status and owner</p>
        </div>
        <button
          onClick={load}
          className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-100 rounded-lg"
        >
          <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
          Refresh
        </button>
      </div>

      {/* Filters — one row above everything they affect */}
      <div className="flex flex-wrap items-center gap-2 mb-5">
        <div className="relative">
          <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Search title or description"
            className={`${inputClass} pl-8 w-60`}
          />
        </div>
        <select className={inputClass} value={filters.category_id} onChange={(e) => setFilter("category_id", e.target.value)}>
          <option value="">All categories</option>
          {categories.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
          <option value="none">Uncategorized</option>
        </select>
        <select className={inputClass} value={filters.owner_id} onChange={(e) => setFilter("owner_id", e.target.value)}>
          <option value="">All owners</option>
          {owners.map((o) => (
            <option key={o.id} value={o.id}>
              {o.name}
            </option>
          ))}
          <option value="none">Unassigned</option>
        </select>
        <select className={inputClass} value={filters.status} onChange={(e) => setFilter("status", e.target.value)}>
          <option value="">All statuses</option>
          <option value="open">Open</option>
          <option value="in_progress">In progress</option>
          <option value="resolved">Resolved</option>
        </select>
        <label className="flex items-center gap-1 text-xs text-gray-500">
          From
          <input type="date" className={inputClass} value={filters.from || ""} onChange={(e) => setFilter("from", e.target.value)} />
        </label>
        <label className="flex items-center gap-1 text-xs text-gray-500">
          To
          <input type="date" className={inputClass} value={filters.to || ""} onChange={(e) => setFilter("to", e.target.value)} />
        </label>
        {hasFilters && (
          <button
            onClick={() => {
              setSearchInput("");
              setFilters({ status: "", category_id: "", owner_id: "" });
            }}
            className="flex items-center gap-1 px-2 py-1.5 text-xs text-gray-500 hover:text-gray-800"
          >
            <X size={12} />
            Clear
          </button>
        )}
      </div>

      {/* Headline counts */}
      <div className="flex flex-wrap gap-3 mb-5">
        <StatTile label="Total" value={totals?.total ?? 0} active={!filters.status} onClick={() => setFilter("status", "")} />
        <StatTile label="Open" value={totals?.open ?? 0} active={filters.status === "open"} onClick={() => setFilter("status", "open")} />
        <StatTile
          label="In progress"
          value={totals?.in_progress ?? 0}
          active={filters.status === "in_progress"}
          onClick={() => setFilter("status", "in_progress")}
        />
        <StatTile
          label="Resolved"
          value={totals?.resolved ?? 0}
          active={filters.status === "resolved"}
          onClick={() => setFilter("status", "resolved")}
        />
        <StatTile
          label={`Open over ${stats?.ageing_days ?? 3} days`}
          value={totals?.overdue ?? 0}
          tone={totals?.overdue ? "text-red-600" : "text-gray-900"}
        />
      </div>

      {/* Breakdowns */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
        <BreakdownTable
          title="By category"
          rows={stats?.by_category || []}
          selectedId={filters.category_id}
          onSelect={(id) => setFilter("category_id", id)}
        />
        <BreakdownTable
          title="By owner (whose bucket)"
          rows={stats?.by_owner || []}
          selectedId={filters.owner_id}
          onSelect={(id) => setFilter("owner_id", id)}
        />
      </div>

      {/* Query list */}
      <div className="text-sm font-medium text-gray-900 mb-2">
        Queries {!loading && <span className="text-gray-500 font-normal">({queries.length})</span>}
      </div>
      {loading ? (
        <div className="flex justify-center py-10">
          <Loader2 size={24} className="animate-spin text-gray-400" />
        </div>
      ) : queries.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 p-8 text-center text-sm text-gray-500">
          No queries match these filters.
        </div>
      ) : (
        <div className="space-y-3">
          {queries.map((q) => (
            <div key={q.id} className="bg-white rounded-xl border border-gray-200 p-4">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="font-medium text-gray-900">{q.subject}</div>
                  <div className="text-xs text-gray-500 mt-0.5">
                    <Link to={`/delivery-hub/schools/${q.slate_id}`} className="text-brand hover:underline">
                      {q.school_name}
                    </Link>
                    {" · "}
                    {new Date(q.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}
                    {q.created_by_name && ` · by ${q.created_by_name}`}
                    {q.days_open != null && (
                      <span className={q.days_open > (stats?.ageing_days ?? 3) ? "text-red-600" : ""}>
                        {" · "}
                        {q.days_open === 0 ? "opened today" : `open ${q.days_open} ${q.days_open === 1 ? "day" : "days"}`}
                      </span>
                    )}
                  </div>
                </div>
                <StatusBadge status={q.status} />
              </div>
              <p className="text-sm text-gray-700 mt-2 whitespace-pre-wrap line-clamp-3">{q.message}</p>
              <AttachmentList attachments={q.attachments} />
              <QueryRoutingControls query={q} categories={categories} owners={owners} onUpdated={replaceQuery} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default DeliveryHubQueries;
